import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
  importProvidersFrom,
  provideAppInitializer,
  inject
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideStore } from '@ngrx/store';
import { reducers, metaReducers } from './state/reducers';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { NgxSpinnerModule } from 'ngx-spinner';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { AuthService } from './services/auth/auth.service';
import { authInterceptor } from './services/auth/auth.interceptor';
import { UserService } from './services/api/user/user.service';
import { analytics } from './events/segment/analytics';
import { getEmailBaseDomain } from './utils/email-utils';
import { firstValueFrom } from 'rxjs';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideStore(reducers, { metaReducers }),
    provideEffects(),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    importProvidersFrom(NgxSpinnerModule.forRoot({ type: 'ball-circus' })),
    provideCharts(withDefaultRegisterables()),
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const userService = inject(UserService);

      await authService.init();

      // Identify user in Segment on every app bootstrap so identity
      // survives page reloads and iOS Safari tab evictions.
      if (authService.isAuthenticated()) {
        try {
          const userProfile = await firstValueFrom(userService.getMe());
          if (userProfile?.userId) {
            const currentUser = authService.getCurrentUser();
            analytics.identify(userProfile.userId, {
              ...(currentUser?.campusId && { sso_campus_id: currentUser.campusId }),
              email_base_domain: getEmailBaseDomain(userProfile.email),
            });
          }
        } catch {
          // Non-critical: app continues without Segment identity
        }
      }
    })
  ]
};
